import React, {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react'
import PropTypes from 'prop-types'
import {
  Accordion as MuiAccordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import {
  AddCircleOutline,
  CheckCircleRounded,
  RemoveCircleOutline,
} from '@mui/icons-material'
import { navigate } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import { BLUE_BASE_OPACITY_40 } from '../../../theme/variables/colors'

// --- Static style objects ---
const boxStyles = { display: 'flex', flexDirection: 'row' }
const inputLabelStyles = {
  fontSize: '18px',
  fontWeight: 500,
  color: 'rgb(9, 42, 72)',
  margin: '0 auto',
}
const arrowImageStyle = { margin: 'auto 0px' }
// Instead of using MUI Grid, we use a Box with CSS grid:
const boxGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  alignItems: 'center',
  width: '100%',
}

const accordionSummarySx = {
  width: '100%',
  borderBottom: 'solid 1px #C8C7CC',
  pr: 1,
  minHeight: 44,
  paddingRight: 10,
}
const accordionDetailsSx = { p: 0 }
const radioGroupSx = { width: '100%' }
const formControlLabelSx = {
  width: '100%',
  justifyContent: 'space-between',
  py: 1,
  px: 2,
  borderBottom: '1px solid #C8C7CC',
}

const menuPropsDefault = {
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  PaperProps: {
    sx: {
      borderRadius: 0,
      border: `1px solid ${BLUE_BASE_OPACITY_40}`,
      boxShadow: 'none',
    },
  },
}

// --- Accessibility helper ---
const SrOnlyLabel = styled('span')(() => ({
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  border: 0,
}))

// --- Custom Input Component for Standard Variant ---
const DropdownInput = React.memo(
  React.forwardRef(function DropdownInput(props, ref) {
    const defaultSx = {
      borderTop: `1px solid ${BLUE_BASE_OPACITY_40}`,
      borderBottom: `1px solid ${BLUE_BASE_OPACITY_40}`,
      borderRight: 'none',
      borderLeft: 'none',
      borderRadius: 0,
      '& .MuiOutlinedInput-notchedOutline': {
        borderWidth: 0,
        borderRadius: 0,
      },
    }
    const sx = defaultSx || props.sx
    return <OutlinedInput {...props} ref={ref} notched={false} sx={sx} />
  })
)
DropdownInput.displayName = 'DropdownInput'

// --- Custom Radio for Accordion Variant ---
const CustomRadio = styled(Radio)(({ theme }) => ({
  color: '#B9B9B9',
  '&.Mui-checked': {
    color: theme.palette.secondary.main,
  },
}))

// --- Styled Accordion for Accordion Variant ---
const StyledAccordion = styled(MuiAccordion)(({ theme }) => ({
  boxShadow: 'none',
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: 'auto',
  },
}))

// --- Separate component for Accordion label content ---
const AccordionLabel = React.memo(({ optionTitle }) => (
  <Box sx={boxStyles}>
    <Typography variant="caption">{optionTitle}</Typography>
  </Box>
))

AccordionLabel.displayName = 'AccordionLabel'
AccordionLabel.propTypes = {
  optionTitle: PropTypes.string.isRequired,
}

// --- Standard Dropdown Variant ---
function StandardDropdown({
  hasTitle = true,
  options,
  handleChange = () => {},
  initialIndex = 0,
  styles = {},
  menuItemStyles = {},
  selectStyles = {},
  selected = null,
  id = 'demo-simple-select-outlined',
  title = null,
}) {
  const uniqueId = useId()
  const [selectedOption, setSelectedOption] = useState(
    options[initialIndex]?.id
  )

  useEffect(() => {
    if (selected) {
      setSelectedOption(selected)
    }
  }, [selected])

  const handleChangeOption = useCallback(
    (event) => {
      setSelectedOption(event.target.value)
      handleChange(event)
    },
    [handleChange]
  )

  const memoFormControlStyles = useMemo(
    () => ({ width: '100%', ...styles }),
    [styles]
  )
  const memoSelectStyles = useMemo(() => selectStyles, [selectStyles])
  const memoMenuItemStyles = useMemo(
    () => ({ justifyContent: 'flex-start' }),
    [menuItemStyles]
  )
  const memoMenuProps = useMemo(() => menuPropsDefault, [])
  const memoBoxGridStyle = useMemo(
    () => ({ display: 'flex', gap: '16px', justifyContent: 'flex-start' }),
    []
  )

  const memoInputLabel = useMemo(() => {
    return title ? (
      <InputLabel id={`${id}-label-${uniqueId}`}>{title.title}</InputLabel>
    ) : (
      <SrOnlyLabel id={`${id}-label-${uniqueId}`}>Select an option</SrOnlyLabel>
    )
  }, [id, title, uniqueId])

  const memoDropdownInput = useMemo(
    () => <DropdownInput label={title ? title.title : ''} />,
    [title]
  )

  const handleMenuItemClick = useCallback(
    (href) => () => {
      if (href) {
        navigate(href)
      }
    },
    []
  )

  return (
    <Box>
      <FormControl variant="outlined" sx={memoFormControlStyles}>
        {memoInputLabel}
        <Select
          labelId={`${id}-label-${uniqueId}`}
          id={`${id}-${uniqueId}`}
          value={selectedOption}
          onChange={handleChangeOption}
          label={title ? title.title : ''}
          input={memoDropdownInput}
          MenuProps={memoMenuProps}
          variant="standard"
          sx={memoSelectStyles}
        >
          {options.map((option, index) => (
            <MenuItem
              key={option.id}
              value={option.id}
              disabled={index === 0 && hasTitle && !option.href}
              sx={memoMenuItemStyles}
              onClick={
                option.href ? handleMenuItemClick(option.href) : undefined
              }
            >
              <Box sx={memoBoxGridStyle}>
                <Typography variant="buttonLg">{option.title}</Typography>
                {selectedOption !== option.id && (option.href || !hasTitle) && (
                  <StaticImage
                    src="../../../images/arrow-right-small-blue.svg"
                    alt="arrow right"
                    layout="fixed"
                    height={12}
                    style={arrowImageStyle}
                  />
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  )
}

// --- Accordion Dropdown Variant ---
function AccordionDropdown({
  hasTitle = true,
  options,
  handleChange = () => {},
  initialIndex = 0,
  styles = {},
  menuItemStyles = {},
  selectStyles = {},
  selected = null,
  id = 'demo-simple-select-outlined',
  title = null,
}) {
  const uniqueId = useId()
  const [selectedOption, setSelectedOption] = useState(
    options[initialIndex]?.id
  )
  const [expanded, setExpanded] = useState(true)
  const accordionSX = useMemo(() => ({ minHeight: 44 }))

  useEffect(() => {
    if (selected) {
      setSelectedOption(selected)
    }
  }, [selected])

  const handleChangeOption = useCallback(
    (event) => {
      setSelectedOption(event.target.value)
      handleChange(event)
    },
    [handleChange]
  )

  const handleMenuItemClick = useCallback(
    (href) => () => {
      if (href) {
        navigate(href)
      }
    },
    []
  )

  const memoExpandIcon = useMemo(
    () =>
      expanded ? (
        <RemoveCircleOutline color="secondary" />
      ) : (
        <AddCircleOutline color="secondary" />
      ),
    [expanded]
  )

  const memoAccordionOnChange = useCallback(
    () => setExpanded((prev) => !prev),
    []
  )
  // Create the label content outside of the prop
  const labelContent = useMemo((title) => (
    <AccordionLabel optionTitle={title} />
  ))

  const radioIcon = useMemo(() => <CheckCircleRounded />)
  const customRadio = useMemo(() => (
    <CustomRadio checkedIcon={radioIcon} onChange={handleChangeOption} />
  ))

  return (
    <StyledAccordion
      square
      expanded={expanded}
      onChange={memoAccordionOnChange}
      elevation={0}
      sx={accordionSX}
    >
      <AccordionSummary
        expandIcon={memoExpandIcon}
        aria-controls={`panel${title?.id || uniqueId}-content`}
        id={`panel${title?.id || uniqueId}-header`}
        sx={accordionSummarySx}
      >
        <Typography variant="body2">
          {title ? title.title : 'Select an option'}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={accordionDetailsSx}>
        <RadioGroup
          row
          aria-label={`${id}-filters`}
          name={`${id}-filters`}
          value={selectedOption}
          onChange={handleChangeOption}
          sx={radioGroupSx}
        >
          {options
            .filter((el) => el.href)
            .map((option) => {
              const memoOnClick = option.href
                ? handleMenuItemClick(option.href)
                : undefined
              return (
                <Fragment key={option.id}>
                  <FormControlLabel
                    value={option.id}
                    control={customRadio}
                    label={labelContent(option.title)}
                    sx={formControlLabelSx}
                    onClick={memoOnClick}
                  />
                </Fragment>
              )
            })}
        </RadioGroup>
      </AccordionDetails>
    </StyledAccordion>
  )
}

// --- Main Exported Component ---
export default function OutlinedDropdown(props) {
  return props.isAccordion ? (
    <AccordionDropdown {...props} />
  ) : (
    <StandardDropdown {...props} />
  )
}

OutlinedDropdown.propTypes = {
  hasTitle: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      href: PropTypes.string,
    })
  ).isRequired,
  handleChange: PropTypes.func,
  initialIndex: PropTypes.number,
  styles: PropTypes.object,
  menuItemStyles: PropTypes.object,
  selectStyles: PropTypes.object,
  selected: PropTypes.string,
  id: PropTypes.string,
  isAccordion: PropTypes.bool,
  title: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string.isRequired,
  }),
}
